import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { from, map, switchMap } from 'rxjs';
import { BillingScope, billingScopes } from '../../../billing-consts';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { OnGetData } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { PricingService } from '../../../services/pricing.service';
import { ProductService } from '../../../services/product.service';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { Product, RecurringInterval } from '../../../types/product.type';
import { Promotion } from '../../../types/promotion.type';
import { CheckoutSidebarComponent } from '../../components/checkout-sidebar/checkout-sidebar.component';
import { CheckoutComponent } from '../../components/checkout/checkout.component';

@Component({
  selector: 'app-checkout-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CheckoutComponent, CheckoutSidebarComponent, ResizeDirective],
  templateUrl: './checkout-dialog.component.html',
  styleUrl: './checkout-dialog.component.scss',
})
export class CheckoutDialogComponent implements OnGetData {
  dialogName = 'checkout';
  private analytics = inject(PICFLOW_ANALYTICS);
  private pricingService = inject(PricingService);
  private productService = inject(ProductService);
  private permissionService = inject(PermissionService);
  product = signal<Product>(undefined);
  promo: Promotion;
  billingFrequency: RecurringInterval;
  startTrialMode = this.pricingService.canStartTrial();
  close: () => void;

  dialogOptions(): DialogOptions {
    return { size: 940, spacingX: 0, spacingY: 0 };
  }

  onGetData(
    data: { product?: Product; promotion?: Promotion; billingFrequency?: RecurringInterval; scope?: BillingScope },
    close: () => void
  ) {
    if (data.product) {
      this.product.set(data.product);
    } else {
      this.permissionService
        .getMinPlanForScope(billingScopes.asset.watermarking)
        .pipe(
          switchMap(productName => {
            return from(this.productService.getAllProducts()).pipe(
              map(products => {
                return products.find(p => p.name.toLowerCase() === productName);
              })
            );
          })
        )
        .subscribe(product => {
          this.product.set(product);
        });
    }
    this.promo = data.promotion;
    this.billingFrequency = data.billingFrequency === 'month' ? 'month' : 'year';
    this.close = close;

    this.analytics.track('initiateCheckout', false, { type: 'page', product: this.product.name });
  }
}
